import { z } from "zod";
import { richTextSchema } from "./blocks";
import { parseEach } from "./sermons";

/*
 * Devotionals (ADR 0077): what the database hands the public site and the portal about a church's devotionals, and the
 * rules every surface shares (what "today" is for a church, the addresses, the status staff see). Nothing here reads
 * the database; `get_public_devotionals` and the page envelope's `devotionals` part do
 * (supabase/migrations/20261030101000_devotionals.sql). Visitors and members only ever receive devotionals that are
 * published and whose day has begun in the church's own time zone.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** A devotional as a card: what the archive, a series page, the block and "previous" and "next" show. */
export const publicDevotionalSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  /** The day it is for, YYYY-MM-DD, in the church's calendar. */
  date: isoDate,
  series: z.object({ slug: z.string().min(1), title: z.string().min(1) }).optional(),
  scripture: z
    .object({
      reference: z.string().min(1).optional(),
      text: z.string().min(1).optional(),
      version: z.string().min(1).optional(),
    })
    .optional(),
  /** The start of the reflection, as plain text, up to 280 characters. */
  excerpt: z.string().optional(),
  /** The name the church publishes. */
  author: z.string().optional(),
  image: z
    .object({ path: z.string().min(1).max(500), alt: z.string().max(200).default("") })
    .optional(),
});
export type PublicDevotional = z.infer<typeof publicDevotionalSchema>;

/** A devotional in full, for its own page, today's on /devotional, and the portal. */
export const publicDevotionalDetailSchema = publicDevotionalSchema.extend({
  /** Structured rich text (ADR 0033); a devotional whose reflection does not parse is drawn without it. */
  reflection: z.unknown().optional(),
  prayer: z.string().optional(),
});
export type PublicDevotionalDetail = z.infer<typeof publicDevotionalDetailSchema>;

/** A series or reading plan with at least one visible devotional. */
export const publicDevotionalSeriesSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  count: z.number().int().nonnegative().default(0),
  firstOn: isoDate.optional(),
  latestOn: isoDate.optional(),
});
export type PublicDevotionalSeries = z.infer<typeof publicDevotionalSeriesSchema>;

/** A devotional's reflection as rich text, or null when it has none or it does not parse. */
export function devotionalReflection(devotional: { readonly reflection?: unknown }) {
  const parsed = richTextSchema.safeParse(devotional.reflection);
  return parsed.success ? parsed.data : null;
}

/** The plain text of a rich-text reflection, paragraphs joined by blank lines: for a portal preview or a share. */
export function devotionalPlainText(devotional: { readonly reflection?: unknown }): string {
  const nodes = devotionalReflection(devotional) ?? [];
  return nodes
    .map((node) => (node.type === "list" ? "" : node.content.map((run) => run.text).join("")))
    .filter((t) => t !== "")
    .join("\n\n");
}

// ------------------------------------------------------------------------------------------------ dates

/** The church's calendar date at an instant (epoch ms), YYYY-MM-DD: a devotional's day begins at the church's midnight. */
export function churchToday(now: number, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone ?? "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(now));
  } catch {
    return new Date(now).toISOString().slice(0, 10);
  }
}

/** "Wednesday, September 16, 2026" in the church's locale. A devotional's date is a calendar date, not an instant. */
export function formatDevotionalDate(
  isoDay: string,
  locale = "en-US",
  style: "long" | "short" = "long",
): string {
  const [y, m, d] = isoDay.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined || Number.isNaN(y + m + d))
    return isoDay;
  return new Intl.DateTimeFormat(locale, {
    ...(style === "long" ? { weekday: "long" } : {}),
    year: "numeric",
    month: style === "long" ? "long" : "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "September 2026": the archive's month headings. */
export function formatDevotionalMonth(isoDay: string, locale = "en-US"): string {
  const [y, m] = isoDay.split("-").map(Number);
  if (y === undefined || m === undefined || Number.isNaN(y + m)) return isoDay.slice(0, 7);
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Devotionals grouped by month, in the order given (newest first in the archive). */
export function devotionalsByMonth<T extends { readonly date: string }>(
  devotionals: readonly T[],
): { readonly month: string; readonly items: readonly T[] }[] {
  const groups: { month: string; items: T[] }[] = [];
  for (const d of devotionals) {
    const month = d.date.slice(0, 7);
    const last = groups.at(-1);
    if (last?.month === month) last.items.push(d);
    else groups.push({ month, items: [d] });
  }
  return groups;
}

/** What staff see a devotional as: a draft, published and waiting for its day (scheduled), or published. */
export type DevotionalStatus = "draft" | "scheduled" | "published";
export function devotionalStatus(
  devotional: { readonly isPublished: boolean; readonly date: string },
  today: string,
): DevotionalStatus {
  if (!devotional.isPublished) return "draft";
  return devotional.date > today ? "scheduled" : "published";
}

// ------------------------------------------------------------------------------------------------ the /devotional pages

const todayViewSchema = z.object({
  kind: z.literal("today"),
  today: isoDate,
  page: z.number().int().positive(),
  pageCount: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  devotional: z.unknown().optional(),
  devotionals: z.array(z.unknown()).default([]),
  series: z.array(z.unknown()).default([]),
});
const seriesViewSchema = z.object({
  kind: z.literal("series"),
  today: isoDate,
  series: z.unknown(),
  devotionals: z.array(z.unknown()).default([]),
});
const oneViewSchema = z.object({
  kind: z.literal("devotional"),
  today: isoDate,
  devotional: z.unknown(),
  previous: z.unknown().optional(),
  next: z.unknown().optional(),
});

export type DevotionalView =
  | {
      readonly kind: "today";
      /** The church's date when the page was read. */
      readonly today: string;
      readonly page: number;
      readonly pageCount: number;
      readonly total: number;
      /** Today's devotional, or the most recent one when none is dated today (`date` < `today`); null for none yet. */
      readonly devotional: PublicDevotionalDetail | null;
      readonly devotionals: readonly PublicDevotional[];
      readonly series: readonly PublicDevotionalSeries[];
    }
  | {
      readonly kind: "series";
      readonly today: string;
      readonly series: PublicDevotionalSeries;
      readonly devotionals: readonly PublicDevotional[];
    }
  | {
      readonly kind: "devotional";
      readonly today: string;
      readonly devotional: PublicDevotionalDetail;
      readonly previous: PublicDevotional | null;
      readonly next: PublicDevotional | null;
    };

/** What `get_public_devotionals` adds to the site's name, brand and menus: the view, or null for nothing visible there. */
export interface DevotionalPages {
  readonly view: DevotionalView | null;
}

function one<T>(schema: z.ZodType<T>, input: unknown): T | null {
  const parsed = schema.safeParse(input);
  return parsed.success ? parsed.data : null;
}

/** Parses `devotionalView` from `get_public_devotionals`; a view that does not parse is "nothing here". */
export function parseDevotionalPages(input: unknown): DevotionalPages {
  const raw = typeof input === "object" && input !== null ? (input as Record<string, unknown>) : {};
  const view = raw["devotionalView"];
  const today = todayViewSchema.safeParse(view);
  if (today.success) {
    return {
      view: {
        ...today.data,
        devotional: one(publicDevotionalDetailSchema, today.data.devotional),
        devotionals: parseEach(publicDevotionalSchema, today.data.devotionals),
        series: parseEach(publicDevotionalSeriesSchema, today.data.series),
      },
    };
  }
  const series = seriesViewSchema.safeParse(view);
  if (series.success) {
    const s = one(publicDevotionalSeriesSchema, series.data.series);
    return {
      view:
        s === null
          ? null
          : {
              kind: "series",
              today: series.data.today,
              series: s,
              devotionals: parseEach(publicDevotionalSchema, series.data.devotionals),
            },
    };
  }
  const single = oneViewSchema.safeParse(view);
  if (single.success) {
    const d = one(publicDevotionalDetailSchema, single.data.devotional);
    return {
      view:
        d === null
          ? null
          : {
              kind: "devotional",
              today: single.data.today,
              devotional: d,
              previous: one(publicDevotionalSchema, single.data.previous),
              next: one(publicDevotionalSchema, single.data.next),
            },
    };
  }
  return { view: null };
}

/** The address of today's devotional page and the archive (page n), a series, and one devotional. */
export const devotionalPaths = {
  today: (options: { page?: number } = {}): string =>
    options.page !== undefined && options.page > 1
      ? `/devotional?page=${String(options.page)}`
      : "/devotional",
  series: (slug: string): string => `/devotional/series/${slug}`,
  devotional: (slug: string): string => `/devotional/${slug}`,
} as const;

/** Whether a request path is one the devotional pages answer. */
export function isDevotionalPath(path: string): boolean {
  return path === "devotional" || path.startsWith("devotional/");
}

/** The envelope's `devotionals` part: the latest visible devotionals and the church's date when it was read. */
export interface DevotionalFeed {
  readonly devotionals: readonly PublicDevotional[];
  readonly today: string | undefined;
}

/**
 * The devotional block's devotional: today's (by the church's date at `now`), or, when `fallback` allows it, the most
 * recent one before today. `isToday` says which, so the block can say "Today's devotional" truthfully.
 */
export function devotionalForFeature(
  devotionals: readonly PublicDevotional[],
  now: number,
  timeZone: string | undefined,
  fallback: boolean,
): { readonly devotional: PublicDevotional; readonly isToday: boolean } | null {
  const today = churchToday(now, timeZone);
  const visible = [...devotionals]
    .filter((d) => d.date <= today)
    .sort((a, b) => b.date.localeCompare(a.date));
  const first = visible[0];
  if (first === undefined) return null;
  if (first.date === today) return { devotional: first, isToday: true };
  return fallback ? { devotional: first, isToday: false } : null;
}

// ------------------------------------------------------------------------------------------------ examples

/** Devotionals for the block's example and the theme conformance test. */
export const EXAMPLE_DEVOTIONALS: readonly PublicDevotional[] = [
  {
    id: "9a8b7c6d-5e4f-4a3b-8c2d-1e0f9a8b7c6d",
    slug: "2026-10-04-life-to-the-full",
    title: "Life to the full",
    date: "2026-10-04",
    series: { slug: "this-season", title: "This season" },
    scripture: {
      reference: "John 10:10",
      text: "I came that they may have life, and may have it abundantly.",
      version: "WEB",
    },
    excerpt: "Jesus did not come to make us busier; he came to give life.",
    author: "Devotional team",
  },
  {
    id: "8b7c6d5e-4f3a-4b2c-9d1e-0f9a8b7c6d5e",
    slug: "2026-10-03-strong-and-courageous",
    title: "Strong and courageous",
    date: "2026-10-03",
    scripture: { reference: "Joshua 1:8-9" },
    excerpt: "Courage is not a feeling; it is a command with a promise.",
  },
];

/** One devotional in full, for the page renderers' conformance test. */
export const EXAMPLE_DEVOTIONAL_DETAIL: PublicDevotionalDetail = {
  ...(EXAMPLE_DEVOTIONALS[0] as PublicDevotional),
  reflection: [
    { type: "paragraph", content: [{ text: "Jesus did not come to make us busier." }] },
    { type: "heading", level: 3, content: [{ text: "Reflect" }] },
    { type: "paragraph", content: [{ text: "Where are you settling for less than life?" }] },
  ],
  prayer: "Lord, teach us to receive the life you came to give.",
};
